import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CloseSquareOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Col, message, Modal, Row, Select } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { TagUpdateRequest } from '@totem/components/software/software_package/types';
import softwareContext from '@totem/components/software/software_report/softwareContext';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
const styles = {
  tag: {
    marginBottom: '3px',
  },
  icon: {
    marginLeft: '5px',
  },
  input: {
    width: '100%',
  },
};
type Props = {
  open: boolean;
};
const DialogSoftwareBulkTags = ({ open }: Props) => {
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isTagsLoading, setIsTagsLoading] = useState<boolean>(false);
  const [selectedOrganization, setSelectedOrganization] =
    useState<string>(emptyId);
  const [showEdit, setShowEdit] = useState<boolean>(open);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [currentTag, setCurrentTag] = useState<string>('');
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { selectedPackages, onAction, setPackageSelected, organizationId } =
    useContext(softwareContext);
  const canAdmin =
    permissions.internalSystemAdministrator.hasPermissions(userProfile);
  useEffect(() => {
    if (!isTagsLoading) {
      setIsTagsLoading(true);
      const uri = `${DEVICES_ENDPOINT}/software/tags`;
      fetch(uri, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: string[]) => {
          setKnownTags(result);
        })
        .then(() => {
          setIsTagsLoading(false);
        });
    }
  }, [getApiToken]);
  const handleClose = () => {
    setCurrentTag('');

    setShowEdit(false);
  };
  const sendUpdate = useCallback(
    async (request: TagUpdateRequest) => {
      if (isSending) {
        return;
      }
      fetch(`${DEVICES_ENDPOINT}/software/tags`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((response) => {
          if (response.status !== 200) {
            message.error('Tag update failed!');
          } else {
            onAction('refresh', null);

            handleClose();
          }
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );
  const handleUpdateTag = (org: string, action: string, tag: string) => {
    if (isNotNull(selectedPackages)) {
      selectedPackages.forEach((pkg) => {
        const request: TagUpdateRequest = {
          action,
          organizationId: org,
          packageId: pkg.id,
          tag,
        };
        sendUpdate(request);
      });

      onAction('clear_package_selections', null);
    }
  };
  const handleSubmit = () => {
    if (currentTag !== null && currentTag !== '') {
      handleUpdateTag(selectedOrganization, 'Add', currentTag);
    }
  };
  const handleChange = (value: string) => {
    setSelectedOrganization(value);
  };
  return (
    <Modal
      open={showEdit}
      onCancel={() => handleClose()}
      title={<ModalTitle>Add Tag</ModalTitle>}
      confirmLoading={false}
      footer={
        <ModalFooter>
          <Button onClick={() => handleClose()}>Close</Button>
          <IBTooltip content={permissions.internalSystemAdministrator.tooltip}>
            <Button
              onClick={handleSubmit}
              type="primary"
              disabled={isSending || !canAdmin}
            >
              Submit
            </Button>
          </IBTooltip>
        </ModalFooter>
      }
    >
      <Select
        onChange={handleChange}
        style={styles.input}
        value={selectedOrganization}
      >
        <Select.Option value={emptyId}>Global</Select.Option>
        {organizationId === userProfile.organization.id && (
          <Select.Option value={userProfile.organization.id}>
            {userProfile.organization.name}
          </Select.Option>
        )}
      </Select>
      <AutoComplete
        placeholder="Tag"
        options={
          isNotNull(knownTags)
            ? knownTags.map((chk) => {
                return { value: chk };
              })
            : []
        }
        value={currentTag}
        onChange={(newValue) => setCurrentTag(newValue)}
        onSelect={(newValue) => setCurrentTag(newValue)}
        style={styles.input}
      />
      <br />
      <br />
      {selectedPackages.map((pkg) => (
        <Row key={pkg.id}>
          <Col span={23}>{pkg.name}</Col>
          <Col span={1}>
            <CloseSquareOutlined
              onClick={() => {
                setPackageSelected(pkg, false);
              }}
            />
          </Col>
        </Row>
      ))}
    </Modal>
  );
};
export default DialogSoftwareBulkTags;
